import React, { useState, useMemo } from 'react';
import { FilterHeader } from './FilterHeader';
import { SearchSection } from './SearchSection';
import { BulkActionsSection } from './BulkActionsSection';
import { ValuesSection } from './ValuesSection';
import { ActionsSection } from './ActionsSection';
import type { Filter } from './ColumnFilterDropdown';

export interface ColumnFilterContentProps {
  fieldName: string;
  options: string[];
  onApply: (
    selectedValues: string[],
    filterMode: 'include' | 'exclude'
  ) => void;
  onCancel?: () => void;
  renderOption?: (option: string, isSelected: boolean) => React.ReactNode;
  currentFilter?: Filter;
  /** Label for the empty option (defaults to "None") */
  emptyOptionLabel?: string;
}

type FilterMode = 'include' | 'exclude';

const ColumnFilterContent: React.FC<ColumnFilterContentProps> = ({
  fieldName,
  options,
  onApply,
  onCancel,
  renderOption,
  currentFilter,
  emptyOptionLabel = 'None',
}) => {
  const [internalSelectedValues, setInternalSelectedValues] = useState<
    string[]
  >(currentFilter?.values || []);
  const [filterMode, setFilterMode] = useState<FilterMode>(
    currentFilter?.condition || 'include'
  );
  const [searchQuery, setSearchQuery] = useState('');

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    return options.filter(option =>
      (option?.toLowerCase() || '').includes(searchQuery.toLowerCase())
    );
  }, [options, searchQuery]);

  const handleSelectAll = () => {
    // Only select the visible filtered options, not all options
    setInternalSelectedValues(filteredOptions);
  };

  const handleClearAll = () => {
    setInternalSelectedValues([]);
  };

  const handleValueChange = (value: string) => {
    // Multi select: toggle selection
    setInternalSelectedValues(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      }
      return [...prev, value];
    });
  };

  const handleApply = () => {
    onApply(internalSelectedValues, filterMode);
  };

  const handleCancel = () => {
    // Reset to the current filter state (preserve existing filter)
    setInternalSelectedValues(currentFilter?.values || []);
    setFilterMode(currentFilter?.condition || 'include');
    setSearchQuery('');
    onCancel?.();
  };

  return (
    <div className="flex flex-col gap-2 py-2 bg-popover rounded-md shadow-md min-w-72 max-w-96">
      <FilterHeader
        fieldName={fieldName}
        filterMode={filterMode}
        onFilterModeChange={setFilterMode}
      />

      <div className="px-3">
        <SearchSection value={searchQuery} onChange={setSearchQuery} />
      </div>

      <div className="px-3">
        <BulkActionsSection
          onSelectAll={handleSelectAll}
          onClearAll={handleClearAll}
        />
      </div>

      <ValuesSection
        options={filteredOptions}
        selectedValues={internalSelectedValues}
        onValueChange={handleValueChange}
        renderOption={renderOption}
        emptyOptionLabel={emptyOptionLabel}
      />

      <div className="px-3">
        <ActionsSection onApply={handleApply} onCancel={handleCancel} />
      </div>
    </div>
  );
};

export default ColumnFilterContent;
